import type { ProjectScope } from '@/features/sync/project-scope';
import {
  enqueueStructuredEntityWrite,
  type StructuredEntityWriteRegistration,
} from '@/features/sync/structured-entity-write';
import { ACTION_ITEM_FLAG_FIELD } from '@/types/shared';
import { createFirestoreDeleteFieldSentinel } from '@/utils/firestore-sanitizer';

import type {
  SpeakerAttribution,
  TranscriptActionAnchor,
  TranscriptSegment,
  TranscriptSession,
} from './types';

function registration(
  conflictPrefix: string,
  allowedKinds: StructuredEntityWriteRegistration['allowedKinds'] = ['patchEntity'],
): StructuredEntityWriteRegistration {
  return {
    feature: 'notes_reports',
    capability: 'EDIT_NOTES',
    conflictPrefix,
    allowedKinds,
    authErrorMessage: 'Authentication required to preserve transcript records',
  };
}

export async function saveTranscriptSession(scope: ProjectScope, session: TranscriptSession): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('transcriptSessions'),
    entityId: session.id,
    kind: 'patchEntity',
    payload: session,
  });
}

export async function saveTranscriptSegment(scope: ProjectScope, segment: TranscriptSegment): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('transcriptSegments'),
    entityId: segment.id,
    kind: 'patchEntity',
    payload: segment,
  });
}

export async function saveTranscriptSegmentActionItemFlag(
  scope: ProjectScope,
  segmentId: string,
  flagged: boolean
): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('transcriptSegments'),
    entityId: segmentId,
    kind: 'patchEntity',
    payload: {
      [ACTION_ITEM_FLAG_FIELD]: flagged ? true : createFirestoreDeleteFieldSentinel(),
    },
  });
}

export async function saveTranscriptActionAnchor(scope: ProjectScope, anchor: TranscriptActionAnchor): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('transcriptActionAnchors'),
    entityId: anchor.id,
    kind: 'patchEntity',
    payload: anchor,
  });
}

export async function saveSpeakerAttribution(scope: ProjectScope, attribution: SpeakerAttribution): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('speakerAttributions', ['patchEntity', 'deleteEntity']),
    entityId: attribution.id,
    kind: 'patchEntity',
    payload: attribution,
  });
}

export async function deleteSpeakerAttribution(scope: ProjectScope, attributionId: string): Promise<void> {
  await enqueueStructuredEntityWrite({
    scope,
    registration: registration('speakerAttributions', ['patchEntity', 'deleteEntity']),
    entityId: attributionId,
    kind: 'deleteEntity',
    payload: { id: attributionId },
  });
}
